import { Button, Chip, Grid, makeStyles, Typography } from "@material-ui/core";
import FiberManualRecordIcon from "@material-ui/icons/FiberManualRecord";
import { tokenVerifyMutation } from "@saleor/auth/mutations";
import {
  VerifyToken,
  VerifyTokenVariables
} from "@saleor/auth/types/VerifyToken";
import { getTokenCookies, getTokens } from "@saleor/auth/utils";
import AppHeader from "@saleor/components/AppHeader";
import { ConfirmButtonTransitionState } from "@saleor/components/ConfirmButton";
import Container from "@saleor/components/Container";
import Form from "@saleor/components/Form";
import FormSpacer from "@saleor/components/FormSpacer";
import PageHeader from "@saleor/components/PageHeader";
import SaveButtonBar from "@saleor/components/SaveButtonBar";
import { SingleAutocompleteChoiceType } from "@saleor/components/SingleAutocompleteSelectField";
import { IS_TOKEN_FROM_COOKIES } from "@saleor/config";
import { ASSIGNEE_CHOICES } from "@saleor/coupons/constants";
import { couponFullDetails_coupon } from "@saleor/coupons/types/CouponFullDetails";
import { MallTags_malls_edges_node } from "@saleor/coupons/types/MallTags";
import {
  canManageCouponUsageLimitByDealType,
  checkCouponPermissionAccess,
  hasAnyCouponUsageLimitPermissionChanged
} from "@saleor/coupons/utils";
import { DiscountErrorFragment } from "@saleor/fragments/types/DiscountErrorFragment";
import { User_userPermissions } from "@saleor/fragments/types/User";
import useNotifier from "@saleor/hooks/useNotifier";
import useStateFromProps from "@saleor/hooks/useStateFromProps";
import useUser from "@saleor/hooks/useUser";
import { sectionNames } from "@saleor/intl";
import { maybe } from "@saleor/misc";
import { SearchCouponCollection_search_edges_node } from "@saleor/searches/types/SearchCouponCollection";
import { FetchMoreProps, UserError } from "@saleor/types";
import {
  CouponAssignee,
  CouponAssigneeEnum,
  CouponDealTypeEnum,
  CouponStatusEnum,
  StaffPromoCreatorEnum
} from "@saleor/types/globalTypes";
import createSingleAutocompleteSelectHandler from "@saleor/utils/handlers/singleAutocompleteSelectChangeHandler";
import { dateTimePhTimeZone } from "@saleor/utils/smop/timeZonePH";
import {
  createCouponValidation,
  draftCouponCreateValidation,
  hasNoError
} from "@saleor/utils/validators/coupon";
import { RawDraftContentState } from "draft-js";
import React, { useEffect, useState } from "react";
import { useMutation } from "react-apollo";
import { FormattedMessage, useIntl } from "react-intl";

import { CouponAccessImage } from "../CouponAccessImage";
import CouponDeepLink from "../CouponDeepLink";
import CouponDetails from "../CouponDetails/CouponDetails";
import CouponEditDialog from "../CouponEditDialog/CouponEditDialog";
import { CouponInformation } from "../CouponInformation";
import CouponPreview from "../CouponPreview";
import CouponPromoImage from "../CouponPromoImage";
import {
  CouponStatus,
  fiberIconColors,
  statusColors
} from "../CouponRankingList/style";
import CouponScheduling from "../CouponScheduling/CouponScheduling";
import CouponTaggingCard from "../CouponTaggingCard/CouponTaggingCard";
import { CouponUsage } from "../CouponUsage";
import { convertTimeFormat, couponTimerOptions } from "../CouponUsage/utils";
import UnlockCodesCard from "../UnlockCodesCard/UnlockCodesCard";

const useStyles = makeStyles(
  theme => ({
    chip: {
      marginLeft: "1em"
    },

    container: {
      alignItems: "stretch",
      display: "flex",
      flexDirection: "row",
      justifyContent: "center",

      [theme.breakpoints.down("sm")]: {
        flexDirection: "column"
      }
    },
    couponTitleChip: {
      display: "flex",
      flexWrap: "wrap"
    },
    createdBy: {
      color: "#747C8C",
      fontSize: "12px",
      fontWeight: 400
    },
    creator: {
      color: "#747C8C",
      fontSize: "12px",
      fontWeight: 800
    },
    gridItem: {
      flex: 1
    },
    pageGrid: {
      display: "grid",
      gridColumnGap: "24px",
      gridRowGap: "24px",
      gridTemplateColumns: "11fr 4fr"
    },
    pageHeader: {
      display: "flex",
      justifyContent: "space-between"
    },
    pageTitle: {
      "& > #txtDetailsPagePageHeader": {
        flex: "unset"
      },
      flexBasis: "100%",
      flexWrap: "wrap",
      marginBottom: "unset"
    },
    previewbutton: {
      marginBottom: "15px"
    },
    sideGrid: {
      width: "100%"
    }
  }),
  { name: "CouponFullDetailsPage" }
);

export interface CouponFullDetailsPageFormData {
  accessImage: string;
  accessImageAlt: string;
  addedCopies: number;
  assignee: CouponAssignee;
  category: string;
  couponClassification: string;
  couponCode: string;
  couponTitle: string;
  mainPromoDescription: RawDraftContentState;
  couponStatus: string;
  couponValue: number;
  isAppExclusive: boolean;
  isCodeUnlocked: boolean;
  isDuplicate: boolean;
  isHidden: boolean;
  isNationwide: boolean;
  isUsageCustomTimer: boolean;
  isUsageTimerApplied: boolean;
  id: string;
  isFeatured: boolean;
  eventEndDate: string;
  eventEndTime: string;
  eventLocation: string;
  eventStartDate: string;
  eventStartTime: string;
  malls: string[];
  hasMalls: boolean;
  mainPromoTitle: string;
  promoImage: string;
  promoImageAlt: string;
  mechanics: RawDraftContentState;
  subtitle: string;
  tagline: string;
  accessPageDescription: RawDraftContentState;
  couponStartDate: string;
  couponStartTime: string;
  couponEndDate: string;
  couponEndTime: string;
  excludedDays: string[];
  occurrence: string;
  redeemType: string;
  redeemingStartDate: string;
  redeemingStartTime: string;
  redeemingEndDate: string;
  redeemingEndTime: string;
  totalCopies: number | "";
  userRestriction: string;
  usageLimit: number | "";
  usageLimitToggle: string;
  usageTimerHour: number;
  usageTimerMinute: number;
  usageTimerSecond: number;
  shortDynamicLink: string;
  deepLinkUrl: string;
  couponCollection: string;
  isExclusiveRedemption: boolean;
}

export interface CouponFormValidation {
  accessImage: string;
  accessImageAlt: string;
  couponCode: string;
  couponTitle: string;
  mainPromoDescription: RawDraftContentState;
  couponStatus: string;
  couponValue: number;
  isHidden: boolean;
  isNationwide: boolean;
  isFeatured: boolean;
  malls: string[];
  mainPromoTitle: string;
  promoImage: string;
  promoImageAlt: string;
  mechanics: RawDraftContentState;
  subtitle: string;
  tagline: string;
  accessPageDescription: RawDraftContentState;
  couponStartDate: string;
  couponStartTime: string;
  couponEndDate: string;
  couponEndTime: string;
  excludedDays: string[];
  occurrence: string;
  redeemType: string;
  redeemingStartDate: string;
  redeemingStartTime: string;
  redeemingEndDate: string;
  redeemingEndTime: string;
  totalCopies: number;
  userRestriction: string;
  usageLimit: number;
  usageTimerHour: number;
  usageTimerMinute: number;
  usageTimerSecond: number;
}

interface CouponFullDetailsProps {
  coupon?: couponFullDetails_coupon;
  errors: DiscountErrorFragment[];
  userPermissions: User_userPermissions[];
  disabled: boolean;
  saveButtonBarState: ConfirmButtonTransitionState;
  malls: MallTags_malls_edges_node[];
  fetchMoreMalls: FetchMoreProps;
  fetchMall: (query: string) => void;
  accessImage: File;
  promoImage: File;
  selectedCategory: string[];
  additionalCopies: number;
  onActivate: () => void;
  onDeactivate: () => void;
  onAdditionalCopies?: (value: number) => void;
  onAccessImageUpload: (file: File) => void;
  onPromoImageUpload: (file: File) => void;
  onBack: () => void;
  onGenerateDeeplink: (id: string) => void;
  fetchMoreCollection?: FetchMoreProps;
  fetchCollections?: (query: string) => void;
  setSelectedCategory: React.Dispatch<React.SetStateAction<any[]>>;
  couponCollections: SearchCouponCollection_search_edges_node[];
  onSubmit: (
    data: CouponFullDetailsPageFormData
  ) => void | Promise<Array<{ code?: string; message?: string }>>;
  onDraftUpdate: (data: CouponFullDetailsPageFormData) => void;
}

interface AdditionalCoupons {
  totalCopies: number;
  added: number;
}

export interface CouponError extends UserError {
  message: string;
}

export const couponErrors: CouponError[] = [
  { field: undefined, message: "Error Coupon Message 1" },
  { field: undefined, message: "Error Coupon Message 2" },
  { field: undefined, message: "Error Coupon Message 3" }
];

export const CouponFullDetails: React.FC<CouponFullDetailsProps> = props => {
  const {
    coupon,
    errors,
    disabled,
    saveButtonBarState,
    malls,
    accessImage,
    promoImage,
    selectedCategory,
    additionalCopies,
    fetchMall,
    fetchMoreMalls,
    onAdditionalCopies,
    onActivate,
    onDeactivate,
    onAccessImageUpload,
    onPromoImageUpload,
    onBack,
    onGenerateDeeplink,
    onSubmit,
    onDraftUpdate,
    couponCollections,
    fetchMoreCollection,
    fetchCollections,
    setSelectedCategory,
    userPermissions
  } = props;
  const classes = useStyles(props);
  const intl = useIntl();
  const [previewCoupon, setPreviewCoupon] = React.useState(false);
  const [editCouponDialog, setEditCouponDialog] = React.useState(false);
  const [hostMallDisplay, setHostMallDisplay] = useState("");
  const { user } = useUser();
  const [currentUserPermissions, setCurrentUserPermissions] = React.useState<
    User_userPermissions[]
  >(
    (userPermissions?.filter(Boolean) as User_userPermissions[]) ||
      user?.userPermissions?.filter(Boolean) ||
      []
  );

  React.useEffect(() => {
    if (userPermissions?.length) {
      setCurrentUserPermissions(
        userPermissions.filter(Boolean) as User_userPermissions[]
      );
    } else if (user?.userPermissions?.length) {
      setCurrentUserPermissions(
        user.userPermissions.filter(Boolean) as User_userPermissions[]
      );
    }
  }, [userPermissions, user?.userPermissions]);

  const [verifyTokenMutation] = useMutation<VerifyToken, VerifyTokenVariables>(
    tokenVerifyMutation
  );

  const isVerifyingRef = React.useRef(false);
  const currentUserPermissionsRef = React.useRef(currentUserPermissions);
  currentUserPermissionsRef.current = currentUserPermissions;

  const lastPermissionUpdateRef = React.useRef(
    typeof window !== "undefined"
      ? localStorage.getItem("coupon_usage_limit_permission_updated") || ""
      : ""
  );

  const refreshPermissions = React.useCallback(
    async (force: boolean = false) => {
      if (isVerifyingRef.current) {
        return;
      }

      const token = IS_TOKEN_FROM_COOKIES
        ? getTokenCookies().auth
        : getTokens().auth;

      if (token) {
        try {
          isVerifyingRef.current = true;
          const verifyResult = await verifyTokenMutation({
            variables: { token }
          });
          const latestPerms =
            verifyResult?.data?.tokenVerify?.user?.userPermissions;
          if (latestPerms && Array.isArray(latestPerms)) {
            const validPerms = latestPerms.filter(
              Boolean
            ) as User_userPermissions[];

            if (
              force ||
              hasAnyCouponUsageLimitPermissionChanged(
                currentUserPermissionsRef.current,
                validPerms
              )
            ) {
              setCurrentUserPermissions(validPerms);
            }
          }
        } catch (err) {
          // Silently ignore error
        } finally {
          isVerifyingRef.current = false;
        }
      }
    },
    [verifyTokenMutation]
  );

  React.useEffect(() => {
    refreshPermissions(true);

    const handleStorage = (event: StorageEvent) => {
      if (event.key === "coupon_usage_limit_permission_updated") {
        lastPermissionUpdateRef.current = event.newValue || "";
        refreshPermissions(true);
      }
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        const currentUpdate =
          localStorage.getItem("coupon_usage_limit_permission_updated") || "";
        if (
          currentUpdate &&
          currentUpdate !== lastPermissionUpdateRef.current
        ) {
          lastPermissionUpdateRef.current = currentUpdate;
          refreshPermissions(true);
        }
      }
    };

    window.addEventListener("storage", handleStorage);
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      window.removeEventListener("storage", handleStorage);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [refreshPermissions]);

  const userHasFeatureCouponAccess = Boolean(
    checkCouponPermissionAccess(currentUserPermissions, "FEATURE_COUPONS")
  );

  const promoCreator = [
    { label: "SMO Marketing", value: StaffPromoCreatorEnum.SMO_MARKETING },
    { label: "Mall Marketing", value: StaffPromoCreatorEnum.MALL_MARKETING },
    { label: "App Development", value: StaffPromoCreatorEnum.APP_DEVELOPMENT }
  ];

  const couponPromoCreator = coupon?.createdBy.promoCreator;

  let assigneeChoices = ASSIGNEE_CHOICES;

  if (couponPromoCreator === StaffPromoCreatorEnum.SMO_MARKETING) {
    assigneeChoices = assigneeChoices.filter(
      assigneeChoice =>
        assigneeChoice.value !== CouponAssigneeEnum.APP_DEVELOPMENT
    );
  } else if (couponPromoCreator === StaffPromoCreatorEnum.MALL_MARKETING) {
    assigneeChoices = assigneeChoices.filter(
      assigneeChoice =>
        assigneeChoice.value === CouponAssigneeEnum.MALL_MARKETING
    );
  } else if (couponPromoCreator === StaffPromoCreatorEnum.APP_DEVELOPMENT) {
    assigneeChoices = assigneeChoices.filter(
      assigneeChoice =>
        assigneeChoice.value === CouponAssigneeEnum.APP_DEVELOPMENT
    );
  }

  const hostMallChoices = maybe(
    () =>
      malls.map(mall => ({
        label: mall?.name,
        value: mall?.id
      })),
    []
  );

  const notify = useNotifier();

  const [validator, setValidator] = React.useState({
    accessPageDescription: null,
    assignee: null,
    category: null,
    couponCode: null,
    couponEndDate: null,
    couponEndTime: null,
    couponStartDate: null,
    couponStartTime: null,
    couponValue: null,
    eventLocation: null,
    eventStartDate: null,
    excludedDays: null,
    mainPromoDescription: null,
    mainPromoTitle: null,
    mechanics: null,
    occurrence: null,
    redeemType: null,
    redeemingEndDate: null,
    redeemingEndTime: null,
    redeemingStartDate: null,
    redeemingStartTime: null,
    subtitle: null,
    tagline: null,
    totalCopies: null,
    usageLimit: null,
    usageTimerHour: null,
    usageTimerMinute: null,
    usageTimerSecond: null
  });

  const isCustomTimerEnabled = maybe(() => {
    const hours = maybe(() => convertTimeFormat(coupon?.usageTimerHour), null);
    const minutes = maybe(
      () => convertTimeFormat(coupon?.usageTimerMinute),
      null
    );
    const seconds = maybe(
      () => convertTimeFormat(coupon?.usageTimerSecond),
      null
    );

    if (hours !== null || minutes !== null || seconds !== null) {
      return !couponTimerOptions.find(
        timerOption => timerOption.value === `${hours}:${minutes}:${seconds}`
      );
    }
  }, false);

  const initialForm: CouponFullDetailsPageFormData = {
    accessImage: maybe(() => coupon?.accessImage.url),
    accessImageAlt: maybe(() => coupon?.accessImage.alt, ""),
    accessPageDescription: maybe(
      () => JSON.parse(coupon?.accessPageDescription || ""),
      null
    ),
    addedCopies: maybe(() => coupon?.quantity, null),
    assignee: maybe(() => coupon?.assignee, null),
    category: maybe(() => coupon?.category, null),
    couponClassification: maybe(() => coupon?.dealType, null),
    couponCode: maybe(() => coupon?.code, ""),
    couponCollection: maybe(() => coupon?.collection?.id, null),
    couponEndDate: coupon?.startDate
      ? dateTimePhTimeZone(maybe(() => coupon?.endDate || "")).date
      : null,
    couponEndTime: coupon?.startDate
      ? dateTimePhTimeZone(maybe(() => coupon?.endDate || "")).time
      : null,
    couponStartDate: coupon?.startDate
      ? dateTimePhTimeZone(maybe(() => coupon?.startDate || "")).date
      : null,
    couponStartTime: coupon?.startDate
      ? dateTimePhTimeZone(maybe(() => coupon?.startDate || "")).time
      : null,
    couponStatus: maybe(() => coupon?.status, null),
    couponTitle: maybe(() => coupon?.name, ""),
    couponValue: maybe(() => coupon?.discountValue, 0),
    deepLinkUrl: "",
    eventEndDate: coupon?.eventStartDate
      ? dateTimePhTimeZone(maybe(() => coupon?.eventStartDate || "")).date
      : null,
    eventEndTime: coupon?.eventStartDate
      ? dateTimePhTimeZone(maybe(() => coupon?.eventStartDate || "")).time
      : null,
    eventLocation: maybe(() => coupon?.eventLocation, ""),
    eventStartDate: coupon?.eventStartDate
      ? dateTimePhTimeZone(maybe(() => coupon?.eventStartDate || "")).date
      : null,
    eventStartTime: coupon?.eventStartDate
      ? dateTimePhTimeZone(maybe(() => coupon?.eventStartDate || "")).time
      : null,
    excludedDays: maybe(() =>
      (coupon?.occurenceExclude || []).map(occurence => occurence.toString())
    ),
    hasMalls: maybe(() => coupon?.hasMalls, true),
    id: maybe(() => coupon?.id, ""),
    isAppExclusive: maybe(() => coupon?.isAppExclusive, false),
    isCodeUnlocked: maybe(() => coupon?.isCodeUnlocked, false),
    isDuplicate: maybe(() => coupon?.isFeatured, false),
    isExclusiveRedemption: maybe(
      () => coupon?.isExclusiveRedemption ?? false,
      false
    ),
    isFeatured: maybe(() => coupon?.isFeatured, false),
    isHidden: maybe(() => coupon?.isHidden, false),
    isNationwide: maybe(() => coupon?.isNationwide, false),
    isUsageCustomTimer: isCustomTimerEnabled,
    isUsageTimerApplied: maybe(() => coupon?.isUsageTimerApplied, false),
    mainPromoDescription: maybe(
      () => JSON.parse(coupon?.mainPromoDescription || ""),
      null
    ),
    mainPromoTitle: maybe(() => coupon?.mainPromoTitle, ""),
    malls: maybe(() => coupon.malls?.edges.map(edge => edge.node.id), []),
    mechanics: maybe(() => JSON.parse(coupon?.mechanics || ""), null),
    occurrence: maybe(() => coupon?.redemptionFrequency, null),
    promoImage: maybe(() => coupon?.promoImage.url, ""),
    promoImageAlt: maybe(() => coupon?.promoImage.alt, ""),
    redeemType: maybe(() => coupon?.redeemType, null),
    redeemingEndDate: coupon?.redeemEndDate
      ? dateTimePhTimeZone(coupon?.redeemEndDate || "").date
      : null,
    redeemingEndTime: coupon?.redeemEndDate
      ? dateTimePhTimeZone(coupon?.redeemEndDate || "").time
      : null,
    redeemingStartDate: coupon?.redeemStartDate
      ? dateTimePhTimeZone(coupon?.redeemStartDate || "").date
      : null,
    redeemingStartTime: coupon?.redeemStartDate
      ? dateTimePhTimeZone(coupon?.redeemStartDate || "").time
      : null,
    shortDynamicLink: "",
    subtitle: maybe(() => coupon?.accessPageSubtitle, ""),
    tagline: maybe(() => coupon?.accessPageTagline, ""),
    totalCopies: coupon?.quantity ?? "",
    usageLimit: maybe(() => coupon?.usageLimit, 1) ?? 1,
    usageLimitToggle: maybe(
      () => ((coupon?.usageLimit ?? 0) > 1 ? "multiple" : "once"),
      "once"
    ),
    usageTimerHour: maybe(() => coupon?.usageTimerHour, 0),
    usageTimerMinute: maybe(() => coupon?.usageTimerMinute, 0),
    usageTimerSecond: maybe(() => coupon?.usageTimerSecond, 0),
    userRestriction: maybe(() => coupon?.userType, null)
  };

  const initialDescription = {
    accessPageDescription: maybe<RawDraftContentState>(() =>
      JSON.parse(coupon?.accessPageDescription)
    ),
    mainPromoDescription: maybe<RawDraftContentState>(() =>
      JSON.parse(coupon?.mainPromoDescription)
    ),
    mechanics: maybe<RawDraftContentState>(() => JSON.parse(coupon?.mechanics))
  };

  const handleValidation = data => {
    const canManageUsageLimit = canManageCouponUsageLimitByDealType(
      currentUserPermissions,
      data.couponClassification || coupon?.dealType
    );
    const validate = createCouponValidation(data, {
      canManageCouponUsageLimit: canManageUsageLimit
    });
    setValidator(validate);
    if (validate.imageUpload) {
      notify({
        status: "error",
        text: validate.imageUpload,
        title: "Coupon Form Error"
      });
    }
    if (validate?.formDataValidations) {
      notify({
        status: "error",
        text: `${validate.formDataValidations?.text}`,
        title: `${validate.formDataValidations.title}`
      });
    }
    return validate;
  };

  const handleDraftUpdateValidation = data => {
    const validate = draftCouponCreateValidation(data);
    setValidator(prevState => ({
      ...prevState,
      ...validate
    }));

    if (validate.mainPromoTitle) {
      notify({
        status: "error",
        text: "Main Promo Title is required when saving a draft",
        title: "Coupon Form Error"
      });
    }

    return validate;
  };

  const handleSubmit = (event: CouponFullDetailsPageFormData) => {
    const validate = handleValidation(event);
    setValidator(validate);
    if (hasNoError(validate)) {
      return onSubmit(event);
    }
  };

  const handlePreviewModal = (data: CouponFullDetailsPageFormData) => {
    const validationResult = handleValidation(data);
    if (hasNoError(validationResult)) {
      setPreviewCoupon(!previewCoupon);
    }
  };

  const handleSubmitUpdateDraft = (data: CouponFullDetailsPageFormData) => {
    const validationResult = handleDraftUpdateValidation(data);
    if (hasNoError(validationResult)) {
      onDraftUpdate(data);
    }
  };

  const handleEditCouponModal = (data: CouponFullDetailsPageFormData) => {
    const validate = handleValidation(data);
    setValidator(validate);
    if (hasNoError(validate)) {
      setEditCouponDialog(!editCouponDialog);
    }
  };

  const [currentDate, setCurrentDate] = useState(new Date());

  useEffect(() => {
    setCurrentDate(new Date());
  }, []);

  const couponDeactivate = () => {
    if (coupon?.endDate) {
      const endDate = new Date(coupon.endDate);

      if (endDate < currentDate) {
        return undefined;
      }
    }

    return coupon.status === CouponStatusEnum.COMPLETED;
  };

  const couponHideSave = () => {
    if (coupon?.endDate) {
      const endDate = new Date(coupon.endDate);

      if (endDate < currentDate) {
        return true;
      }
    }

    return false;
  };

  const [
    couponCollectionDisplayName,
    setCouponCollectionDisplayName
  ] = useStateFromProps(maybe(() => coupon?.collection?.title, ""));

  const collectionChoiceList: SingleAutocompleteChoiceType[] = couponCollections.map(
    item => ({
      label: item.title,
      value: item.id
    })
  );

  const showDeactivateButton =
    coupon?.status === CouponStatusEnum.ACTIVE ||
    coupon?.status === CouponStatusEnum.SCHEDULED ||
    coupon?.status === CouponStatusEnum.EXPIRED;

  return (
    <Form initial={initialForm} onSubmit={handleSubmit} confirmLeave>
      {({ data, change, hasChanged, submit }) => {
        React.useEffect(() => {
          const values = {
            added: Number(additionalCopies),
            totalCopies: Number(coupon?.quantity)
          } as AdditionalCoupons;
          const addedTotal = values.totalCopies + values.added;
          if (addedTotal >= 0) {
            change({
              target: {
                name: "addedCopies",
                value: addedTotal
              }
            });
          }
        }, [additionalCopies]);

        React.useEffect(() => {
          if (
            (data?.category !== coupon?.category && data?.category !== null) ||
            data?.couponClassification !== coupon?.dealType
          ) {
            setCouponCollectionDisplayName("");
            change({
              target: {
                name: "couponCollection",
                value: null
              }
            });
            change({
              target: { name: "isExclusiveRedemption", value: false }
            });
          }
        }, [data.category, data.couponClassification]);

        const handleSelectCollection = createSingleAutocompleteSelectHandler(
          change,
          setCouponCollectionDisplayName,
          collectionChoiceList
        );

        const handleSelectHostMall = createSingleAutocompleteSelectHandler(
          change,
          setHostMallDisplay,
          hostMallChoices
        );

        const handleChangeWithCouponCollectionClear = event => {
          change(event);
          change({ target: { name: "couponCollection", value: "" } });
          change({ target: { name: "isExclusiveRedemption", value: false } });
          setCouponCollectionDisplayName("");
          if (event.target.name === "couponClassification") {
            change({ target: { name: "malls", value: [] } });
            setHostMallDisplay("");
          }
          if (event.target.value === CouponDealTypeEnum.EVENTS) {
            change({
              target: {
                name: "isAppExclusive",
                value: false
              }
            });
          }
        };

        return (
          <Container>
            <AppHeader onBack={onBack}>
              {intl.formatMessage(sectionNames.coupons)}
            </AppHeader>
            <div className={classes.pageHeader}>
              <div className={classes.couponTitleChip}>
                <PageHeader title={"Coupons"} className={classes.pageTitle}>
                  <div className={classes.chip}>
                    {coupon && (
                      <Chip
                        label={CouponStatus[coupon.status]}
                        style={{ backgroundColor: statusColors[coupon.status] }}
                        icon={
                          <FiberManualRecordIcon
                            style={{ color: fiberIconColors[coupon.status] }}
                          />
                        }
                      />
                    )}
                  </div>
                </PageHeader>
                <Typography className={classes.createdBy} variant="subtitle1">
                  {"Created by: "}
                  <Typography component="span" className={classes.creator}>
                    {coupon?.createdBy.firstName} {coupon?.createdBy.lastName}{" "}
                    {`(${
                      promoCreator.find(
                        item => item.value === coupon?.createdBy.promoCreator
                      )?.label
                    })`}
                  </Typography>
                </Typography>
              </div>
              <Button
                id="previewModalBtn"
                onClick={() => handlePreviewModal(data)}
                variant="outlined"
                color="primary"
                className={classes.previewbutton}
              >
                <FormattedMessage
                  defaultMessage="PREVIEW COUPON"
                  description="button"
                />
              </Button>
            </div>
            <Grid container className={classes.pageGrid}>
              <div>
                <CouponDetails
                  assigneeChoices={assigneeChoices}
                  data={data}
                  coupon={coupon}
                  disabled={
                    coupon?.status === "ACTIVE" ||
                    coupon?.status === "COMPLETED" ||
                    coupon?.status === "DELETED" ||
                    coupon?.status === "INACTIVE" ||
                    coupon?.status === "SCHEDULED" ||
                    coupon?.status === "EXPIRED"
                  }
                  onChange={change}
                  fetchMalls={fetchMall}
                  fetchMoreMalls={fetchMoreMalls}
                  additionalCopies={additionalCopies}
                  onAdditionalCopies={onAdditionalCopies}
                  malls={malls}
                  error={validator}
                  onMallChange={handleSelectHostMall}
                  hostMall={hostMallChoices}
                  hostMallDisplay={hostMallDisplay}
                  setSelectedCategory={setSelectedCategory}
                  couponCollections={collectionChoiceList}
                  fetchMoreCollection={fetchMoreCollection}
                  fetchCollections={fetchCollections}
                  onCouponCollectionChange={handleSelectCollection}
                  couponCollectionDisplayValue={couponCollectionDisplayName}
                  selectedCategory={selectedCategory}
                  onCouponCategoryChange={handleChangeWithCouponCollectionClear}
                  onCouponClassificationChange={
                    handleChangeWithCouponCollectionClear
                  }
                />
                <Grid container spacing={2} className={classes.container}>
                  <Grid item xs={12} sm={6} className={classes.gridItem}>
                    <CouponPromoImage
                      data={data}
                      errors={errors}
                      disabled={
                        coupon?.status === "INACTIVE" ||
                        coupon?.status === "DRAFT"
                          ? disabled
                          : true
                      }
                      onChange={change}
                      onPromoImageUpload={onPromoImageUpload}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} className={classes.gridItem}>
                    <CouponAccessImage
                      data={data}
                      errors={errors}
                      disabled={
                        coupon?.status === "INACTIVE" ||
                        coupon?.status === "DRAFT"
                          ? disabled
                          : true
                      }
                      onChange={change}
                      onAccessImageUpload={onAccessImageUpload}
                    />
                  </Grid>
                </Grid>
                <UnlockCodesCard
                  couponId={data.id as string}
                  couponName={data.couponTitle as string}
                  isEnabledUnlockCodes={data.isCodeUnlocked}
                  onChange={change}
                  isCouponEditable={
                    coupon?.status === "DRAFT" || coupon?.status === "INACTIVE"
                  }
                />
                <FormSpacer />
                <CouponInformation
                  data={data}
                  disabled={
                    coupon?.status === "INACTIVE" || coupon?.status === "DRAFT"
                      ? disabled
                      : true
                  }
                  errors={couponErrors}
                  localError={validator}
                  initialDescription={initialDescription}
                  onChange={change}
                />
                <CouponScheduling
                  data={data}
                  onChange={change}
                  localError={validator}
                  disabled={
                    coupon?.status === "INACTIVE" || coupon?.status === "DRAFT"
                      ? disabled
                      : true
                  }
                />
              </div>
              <div className={classes.sideGrid}>
                {userHasFeatureCouponAccess && (
                  <CouponTaggingCard
                    isFeatured={data.isFeatured}
                    isAppExclusive={data.isAppExclusive}
                    isAppExclusiveHidden={
                      data.couponClassification === CouponDealTypeEnum.EVENTS
                    }
                    onChange={change}
                    isEditableState={
                      coupon?.status === "DRAFT" ||
                      coupon?.status === "INACTIVE"
                    }
                  />
                )}
                <CouponUsage
                  data={data}
                  disabled={
                    coupon?.status === "ACTIVE" ||
                    coupon?.status === "COMPLETED" ||
                    coupon?.status === "DELETED" ||
                    coupon?.status === "INACTIVE" ||
                    coupon?.status === "SCHEDULED" ||
                    coupon?.status === "EXPIRED"
                  }
                  onChange={change}
                  localError={validator}
                  userPermissions={currentUserPermissions}
                />
                <FormSpacer />
                <CouponDeepLink
                  linkId={coupon && coupon.id}
                  linkUrl={coupon && coupon.deeplinkUrl}
                  shortLinkUrl={coupon && coupon.shortDynamicLink}
                  onGenerateDeeplink={onGenerateDeeplink}
                />
              </div>
              <CouponPreview
                data={data}
                open={previewCoupon}
                onClose={() => setPreviewCoupon(false)}
                accessPageImg={accessImage}
                mainPromoImg={promoImage}
                malls={malls}
              />
              <CouponEditDialog
                open={editCouponDialog}
                onClose={() => setEditCouponDialog(false)}
                onSave={submit}
              />
            </Grid>
            <div>
              {coupon && (
                <>
                  {coupon.status === CouponStatusEnum.DRAFT ? (
                    <SaveButtonBar
                      state={saveButtonBarState}
                      onCancel={onBack}
                      onSave={submit}
                      disabled={!hasChanged}
                      hideSave={true}
                      onDraft={() => handleSubmitUpdateDraft(data)}
                      onDelete={undefined}
                      onPublish={submit}
                    />
                  ) : coupon.status === CouponStatusEnum.INACTIVE ? (
                    <SaveButtonBar
                      state={saveButtonBarState}
                      onCancel={onBack}
                      onSave={() => handleEditCouponModal(data)}
                      onActivate={onActivate}
                      hideActivate={coupon.status === CouponStatusEnum.INACTIVE}
                      disabled={!hasChanged}
                      saveButtonVariant={true}
                      disableAlternateButton={disabled || !hasChanged}
                    />
                  ) : coupon.status === CouponStatusEnum.COMPLETED ? (
                    <SaveButtonBar
                      state={saveButtonBarState}
                      onCancel={onBack}
                      onSave={submit}
                      onDeactivate={onDeactivate}
                      hideDeactivate={couponDeactivate()}
                      disabled={!hasChanged}
                      saveButtonVariant={true}
                      hideSave={couponHideSave()}
                      disableAlternateButton={disabled || !hasChanged}
                    />
                  ) : (
                    <SaveButtonBar
                      state={saveButtonBarState}
                      onCancel={onBack}
                      onSave={submit}
                      onDeactivate={onDeactivate}
                      hideDeactivate={showDeactivateButton}
                      disableAlternateButton={true}
                      saveButtonVariant={true}
                      disabled={!hasChanged}
                    />
                  )}
                </>
              )}
            </div>
          </Container>
        );
      }}
    </Form>
  );
};
export default CouponFullDetails;
